import { Icon, Menu, MenuItem, Typography } from '@mui/material'

import { postStat } from 'api/stats/stats.api'
import { type DatasetFillMethod } from 'helpers/dataset.helper/dataset.helper.types'
import { exportToExcel } from 'helpers/dataset.helper/datasetExcelExport.helper'
import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import TooltipIconButton from 'ui/components/TooltipIconButton/TooltipIconButton'

import styles from './ExportDatasetsMenu.module.less'

type ExportDatasetsMenuProps = {
  datasets: Dataset[]
  datasetNames: Record<string, string>
  excelFillMethod?: DatasetFillMethod
  onImageExport?: () => void
  onPrint?: () => void
}

export default function ExportDatasetsMenu({
  datasets,
  datasetNames,
  excelFillMethod,
  onImageExport,
  onPrint,
}: ExportDatasetsMenuProps): ReactElement {
  const { t } = useTranslation()

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)

  function handleClick(event: React.MouseEvent): void {
    setAnchorEl(event.currentTarget as HTMLButtonElement)
  }

  function handleClose(): void {
    setAnchorEl(null)
  }

  return (
    <>
      <TooltipIconButton
        tooltip={t(`Export`)}
        icon={
          <Icon
            color="primary"
            className={styles.ExportDatasetsMenu_icon + ` fas fa-cloud-download-alt`}
            fontSize="small"
          />
        }
        onClick={handleClick}
        aria-controls="simple-menu"
        aria-haspopup="true"
      />
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        className={styles.ExportDatasetsMenu}
      >
        <MenuItem
          onClick={() => {
            exportToExcel(datasets, datasetNames, { fillMethod: excelFillMethod })
            postStat(`chart`, `export-to-excel`)
            handleClose()
          }}
          className={styles.ExportDatasetsMenuItem}
        >
          <Icon color="primary" className={styles.ExportDatasetsMenuItem_icon + ` far fa-file-excel`} />
          <Typography className={styles.ExportDatasetsMenuItem_text} variant="body1">
            {t(`Excel`)}
          </Typography>
        </MenuItem>

        {onImageExport && (
          <MenuItem
            onClick={() => {
              onImageExport()
              postStat(`chart`, `export-to-png`)
              handleClose()
            }}
            className={styles.ExportDatasetsMenuItem}
          >
            <Icon color="primary" className={styles.ExportDatasetsMenuItem_icon + ` far fa-file-image`} />
            <Typography className={styles.ExportDatasetsMenuItem_text} variant="body1">
              {t(`Image (PNG)`)}
            </Typography>
          </MenuItem>
        )}

        {onPrint && (
          <MenuItem
            onClick={() => {
              onPrint()
              postStat(`chart`, `export-to-print`)
              handleClose()
            }}
            className={styles.ExportDatasetsMenuItem}
          >
            <Icon color="primary" className={styles.ExportDatasetsMenuItem_icon + ` far fa-print`} />
            <Typography className={styles.ExportDatasetsMenuItem_text} variant="body1">
              {t(`Print`)}
            </Typography>
          </MenuItem>
        )}
      </Menu>
    </>
  )
}
